import Vue from 'vue';
import VueRouter from 'vue-router';

import Header from '@/components/header/Header.vue';
import Footer from '@/components/footer/Footer.vue';
import AccountActionsPanel from '@/components/account/privateInfo/AccountActionsPanel.vue';
import SignUpFooter from '@/components/footer/SignUpFooter.vue';

import Base from '../views/Base.vue';
import LoginBase from '../views/LoginBase.vue';
import Home from '../views/Home.vue';
import AppContent from '../views/AppContent.vue';
import AccountContent from '../views/AccountContent.vue';
import TargetingInfo from '../views/TargetingInfo.vue';
import PrivateInfo from '../views/PrivateInfo.vue';
import DigitalFingerprint from '../views/DigitalFingerprint.vue';
import Login from '../views/Login.vue';
import Options from '../views/Options.vue';

import { ROUTER_PATHS } from './constants';

import authGuard from './guards/authGuard';

import logoutHandler from './handlers/logoutHandler';
import zohoHandler from './handlers/zohoHandler';
import { notifySignUpSuccess } from '../services/embed';

Vue.use(VueRouter);

const routes = [
  {
    path: '/a',
    component: LoginBase,
    redirect: ROUTER_PATHS.LOGIN,
    children: [
      {
        path: ROUTER_PATHS.LOGIN,
        components: {
          content: Login,
          footer: SignUpFooter,
        },
      },
      {
        path: ROUTER_PATHS.SIGN_UP,
        meta: {
          hideZohoChat: true,
        },
        components: {
          content: () => import(/* webpackChunkName: "signup" */ '../views/SignUp.vue'),
          footer: () =>
            import(/* webpackChunkName: "signup" */ '../components/footer/LoginFooter.vue'),
        },
      },
      {
        path: ROUTER_PATHS.SUCCESS_REGISTRATION,
        meta: {
          hideZohoChat: true,
        },
        components: {
          content: () =>
            import(/* webpackChunkName: "signup" */ '../views/SuccessRegistration.vue'),
        },
      },
      {
        path: ROUTER_PATHS.EMAIL_ACTIONS,
        components: {
          content: () => import(/* webpackChunkName: "signup" */ '../views/EmailActions.vue'),
        },
      },
      {
        path: ROUTER_PATHS.RECOVERY_PASSWORD,
        components: {
          content: () =>
            import(/* webpackChunkName: "recovery-password" */ '../views/RecoveryPassword.vue'),
          footer: () =>
            import(
              /* webpackChunkName: "recovery-password" */ '../components/footer/RecoveryPasswordFooter.vue'
            ),
        },
      },
      {
        path: ROUTER_PATHS.SUCCESS_RECOVERY_PASSWORD,
        components: {
          content: () =>
            import(
              /* webpackChunkName: "recovery-password" */ '../views/RecoveryPasswordSuccess.vue'
            ),
        },
      },
      {
        path: ROUTER_PATHS.OPTIONS,
        components: {
          content: Options,
        },
      },
    ],
  },
  {
    path: ROUTER_PATHS.HOME,
    component: Base,
    meta: {
      extension: true,
    },
    children: [
      {
        path: '',
        components: {
          header: Header,
          content: AppContent,
          footer: Footer,
        },
        children: [
          {
            path: '',
            name: 'Home',
            component: Home,
          },
          {
            path: ROUTER_PATHS.PROBLEMS,
            name: 'Problems',
            component: () =>
              import(/* webpackChunkName: "app-description" */ '../views/Problems.vue'),
          },
          {
            path: ROUTER_PATHS.REWARDS,
            name: 'Rewards',
            component: () =>
              import(/* webpackChunkName: "app-description" */ '../views/Rewards.vue'),
          },
          {
            path: ROUTER_PATHS.PROMISES,
            name: 'Promises',
            component: () =>
              import(/* webpackChunkName: "app-description" */ '../views/Promises.vue'),
          },
          {
            path: ROUTER_PATHS.TARGETING,
            name: 'Targeting',
            component: () =>
              import(/* webpackChunkName: "app-description" */ '../views/Targeting.vue'),
          },
          {
            path: ROUTER_PATHS.CONTACTS,
            name: 'Contacts',
            component: () =>
              import(/* webpackChunkName: "app-description" */ '../views/Contacts.vue'),
          },
          {
            path: ROUTER_PATHS.UNINSTALL_EXTENSION,
            name: 'Uninstall Extension',
            component: () =>
              import(
                /* webpackChunkName: "uninstall-extension" */ '../views/UninstallExtension.vue'
              ),
            meta: {
              extension: false,
            },
          },
          {
            path: ROUTER_PATHS.REFERRALS,
            name: 'Referrals',
            component: () => import(/* webpackChunkName: "referrals" */ '../views/Referrals.vue'),
            meta: {
              safe: true,
            },
          },
        ],
      },
      {
        path: ROUTER_PATHS.ACCOUNT,
        components: {
          header: Header,
          content: AccountContent,
          footer: Footer,
        },
        redirect: ROUTER_PATHS.TARGETING_INFO,
        children: [
          {
            path: ROUTER_PATHS.TARGETING_INFO,
            name: 'Targeting info',
            components: {
              default: TargetingInfo,
              actions: AccountActionsPanel,
            },
            meta: {
              safe: true,
            },
          },
          {
            path: ROUTER_PATHS.PRIVATE_INFO,
            name: 'Private info',
            components: {
              default: PrivateInfo,
              actions: AccountActionsPanel,
            },
            meta: {
              safe: true,
            },
          },
          {
            path: ROUTER_PATHS.DIGITAL_FINGERPRINT,
            name: 'Digital fingerprint',
            components: {
              default: DigitalFingerprint,
              actions: AccountActionsPanel,
            },
            meta: {
              safe: true,
            },
          },
        ],
      },
    ],
  },
  {
    path: '/ad',
    component: Base,
    children: [
      {
        path: '',
        components: {
          header: Header,
          content: AppContent,
          footer: Footer,
        },
        children: [
          {
            path: '/adguard-adblocker',
            component: () =>
              import(/* webpackChunkName: "adblocker" */ '../views/ad/AdBlockInstructions.vue'),
            props: { type: 'adguard' },
          },
          {
            path: '/adblock-plus',
            component: () =>
              import(/* webpackChunkName: "adblocker" */ '../views/ad/AdBlockInstructions.vue'),
            props: { type: 'adblockPlus' },
          },
          {
            path: '/ublock-origin',
            component: () =>
              import(/* webpackChunkName: "adblocker" */ '../views/ad/AdBlockInstructions.vue'),
            props: { type: 'ublockOrigin' },
          },
          {
            path: '/ublock',
            component: () =>
              import(/* webpackChunkName: "adblocker" */ '../views/ad/AdBlockInstructions.vue'),
            props: { type: 'ublock' },
          },
          {
            path: '/adblock',
            component: () =>
              import(/* webpackChunkName: "adblocker" */ '../views/ad/AdBlockInstructions.vue'),
            props: { type: 'adblock' },
          },
          {
            path: '/ad-blocker',
            component: () =>
              import(/* webpackChunkName: "adblocker" */ '../views/ad/AdBlockInstructions.vue'),
            props: { type: 'adblocker' },
          },
          {
            path: '/fair-adblocker',
            component: () =>
              import(/* webpackChunkName: "adblocker" */ '../views/ad/AdBlockInstructions.vue'),
            props: { type: 'fairAdblocker' },
          },
          {
            path: '/ads-killer-adblocker-plus',
            component: () =>
              import(/* webpackChunkName: "adblocker" */ '../views/ad/AdBlockInstructions.vue'),
            props: { type: 'adsKiller' },
          },
        ],
      },
    ],
  },
  {
    path: '/signup/form',
    component: () => import('../views/SignUp.vue'),
    props: {
      onSuccess: notifySignUpSuccess,
    },
  },
  {
    path: '/ab',
    component: Base,
    meta: {
      canAuthorize: true,
      hideZohoChat: true,
    },
    children: [
      {
        path: '/ab/a',
        components: {
          header: () => import('../views/ab/Header.vue'),
          content: () => import(/* webpackChunkName: "AB" */ '../views/ab/A.vue'),
        },
      },
      {
        path: '/ab/b',
        components: {
          header: () => import('../views/ab/Header.vue'),
          content: () => import(/* webpackChunkName: "AB" */ '../views/ab/B.vue'),
        },
      },
      {
        path: '/ab/d',
        components: {
          header: () => import('../views/ab/Header.vue'),
          content: () => import(/* webpackChunkName: "AB" */ '../views/ab/D.vue'),
        },
      },
      {
        path: '/ab/e',
        components: {
          header: () => import('../views/ab/Header.vue'),
          content: () => import(/* webpackChunkName: "AB" */ '../views/ab/E.vue'),
        },
      },
      {
        path: '/ab/f',
        components: {
          header: () => import('../views/ab/Header.vue'),
          content: () => import(/* webpackChunkName: "AB" */ '../views/ab/F.vue'),
        },
      },
      {
        path: '/ab/mobile/a',
        components: {
          content: () => import(/* webpackChunkName: "AB" */ '../views/ab/MobileA.vue'),
        },
      },
      {
        path: '/ab/mobile/b',
        components: {
          content: () => import(/* webpackChunkName: "AB" */ '../views/ab/MobileB.vue'),
        },
      },
      {
        path: '/ab/mobile/c',
        components: {
          content: () => import(/* webpackChunkName: "AB" */ '../views/ab/MobileC.vue'),
        },
      },
    ],
  },
  {
    path: '*',
    redirect: '/',
  },
];

const router = new VueRouter({
  base: process.env.BASE_URL,
  routes,
  scrollBehavior: () => ({ x: 0, y: 0 }),
});

router.beforeEach(authGuard);
router.beforeEach(zohoHandler);

logoutHandler(router);

export default router;
